import React, { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  message,
  Typography,
  Alert,
  Space,
  Divider,
  Table,
  Tag,
  Progress
} from 'antd';
import {
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { journalService, BulkUploadResult } from '@/services/journalService';

const { Text, Title, Paragraph } = Typography;

interface JournalEntryBulkUploadProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const JournalEntryBulkUpload: React.FC<JournalEntryBulkUploadProps> = ({
  visible,
  onClose,
  onSuccess
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select a file to upload');
      return;
    }

    const file = fileList[0] as unknown as File;
    setUploading(true);
    setUploadResult(null);

    try {
      const result = await journalService.bulkUploadJournalEntries(file);
      setUploadResult(result);

      if (result.entriesCreated > 0) {
        message.success(`Successfully created ${result.entriesCreated} journal entries`);
        onSuccess();
      } else if (result.entriesSkipped > 0) {
        message.info(`All ${result.entriesSkipped} entries were already imported (duplicates skipped)`);
      } else if (result.errors.length > 0) {
        message.error(`Upload completed with ${result.errors.length} errors`);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFileList([]);
    setUploadResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    // Create sample CSV template
    const headers = [
      'Number',
      'Partner',
      'Reference',
      'Journal (code)',
      'Cheque No',
      'Invoice No',
      'State',
      'Date',
      'Account (code)',
      'Label',
      'Debit',
      'Credit'
    ];
    const sampleRow = [
      'JE/2024/0001',
      'ABC Corp',
      'INV-001',
      'SALE',
      '',
      'INV-001',
      'Posted',
      '2024-01-15',
      '1100',
      'Sales Revenue',
      '',
      '1000.00'
    ];
    const sampleRow2 = [
      'JE/2024/0001',
      'ABC Corp',
      'INV-001',
      'SALE',
      '',
      'INV-001',
      'Posted',
      '2024-01-15',
      '4100',
      'Accounts Receivable',
      '1000.00',
      ''
    ];

    const csvContent = [
      headers.join(','),
      sampleRow.join(','),
      sampleRow2.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'journal_entries_template.csv';
    link.click();
  };

  const errorColumns = [
    {
      title: 'Entry Name',
      dataIndex: 'entryName',
      key: 'entryName',
      width: 200
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      render: (text: string) => <Text type="danger">{text}</Text>
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: '#52c41a' }} />
          <span>Bulk Upload Journal Entries</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {uploadResult ? 'Close' : 'Cancel'}
        </Button>,
        !uploadResult && (
          <Button
            key="upload"
            type="primary"
            loading={uploading}
            onClick={handleUpload}
            disabled={fileList.length === 0}
            icon={<UploadOutlined />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        )
      ]}
    >
      {!uploadResult ? (
        <div>
          <Alert
            type="info"
            showIcon
            message="Supported Format"
            description={
              <div>
                <Paragraph style={{ marginBottom: 8 }}>
                  Upload an Excel file (.xlsx) with the following columns:
                </Paragraph>
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li><Text code>Number</Text> - Entry number (e.g., JE/2024/0001)</li>
                  <li><Text code>Partner</Text> - Partner/Customer name</li>
                  <li><Text code>Reference</Text> - Reference number</li>
                  <li><Text code>Journal (code)</Text> - Journal code (e.g., SALE, BANK)</li>
                  <li><Text code>Cheque No</Text> - Cheque number (optional)</li>
                  <li><Text code>Invoice No</Text> - Invoice number (optional)</li>
                  <li><Text code>State</Text> - Entry state (Draft/Posted/Cancel)</li>
                  <li><Text code>Date</Text> - Entry date</li>
                  <li><Text code>Account (code)</Text> - Account code</li>
                  <li><Text code>Label</Text> - Line description</li>
                  <li><Text code>Debit</Text> - Debit amount</li>
                  <li><Text code>Credit</Text> - Credit amount</li>
                </ul>
              </div>
            }
            style={{ marginBottom: 16 }}
          />

          <Divider />

          <Upload.Dragger
            fileList={fileList}
            beforeUpload={(file) => {
              const isExcel =
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls');

              if (!isExcel) {
                message.error('Only Excel files (.xlsx, .xls) are allowed');
                return Upload.LIST_IGNORE;
              }

              setFileList([file as unknown as UploadFile]);
              return false; // Prevent auto upload
            }}
            onRemove={() => {
              setFileList([]);
            }}
            maxCount={1}
            accept=".xlsx,.xls"
          >
            <p className="ant-upload-drag-icon">
              <FileExcelOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            </p>
            <p className="ant-upload-text">Click or drag Excel file to this area</p>
            <p className="ant-upload-hint">
              Support for single .xlsx or .xls file upload
            </p>
          </Upload.Dragger>

          <Divider />

          <Button
            type="link"
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
          >
            Download Template (CSV)
          </Button>
        </div>
      ) : (
        <div>
          {/* Upload Summary */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {uploadResult.entriesCreated > 0 ? (
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
            ) : uploadResult.errors.length > 0 ? (
              <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14' }} />
            ) : (
              <CheckCircleOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            )}
            <Title level={4} style={{ marginTop: 16 }}>
              Upload Complete
            </Title>
          </div>

          {/* Statistics */}
          <div style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Total Rows Processed:</Text>
                <Text strong>{uploadResult.totalRows}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Entries Created:</Text>
                <Tag color="success">{uploadResult.entriesCreated}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Duplicates Skipped:</Text>
                <Tag color="warning">{uploadResult.entriesSkipped}</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Errors:</Text>
                <Tag color={uploadResult.errors.length > 0 ? 'error' : 'default'}>
                  {uploadResult.errors.length}
                </Tag>
              </div>
            </Space>

            <Progress
              percent={Math.round(
                ((uploadResult.entriesCreated + uploadResult.entriesSkipped) /
                  Math.max(uploadResult.totalRows, 1)) *
                  100
              )}
              status={uploadResult.errors.length > 0 ? 'exception' : 'success'}
              style={{ marginTop: 16 }}
            />
          </div>

          {/* Skipped Duplicates */}
          {uploadResult.skippedDuplicates.length > 0 && (
            <Alert
              type="info"
              showIcon
              message={`${uploadResult.skippedDuplicates.length} entries already exist`}
              description={
                <div style={{ maxHeight: 100, overflow: 'auto' }}>
                  <Text type="secondary">
                    {uploadResult.skippedDuplicates.join(', ')}
                  </Text>
                </div>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Errors Table */}
          {uploadResult.errors.length > 0 && (
            <div>
              <Divider>Errors</Divider>
              <Table
                dataSource={uploadResult.errors}
                columns={errorColumns}
                size="small"
                pagination={{ pageSize: 5 }}
                rowKey={(record) => `${record.entryName}-${record.error}`}
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default JournalEntryBulkUpload;
